// src/services/api.js
const BASE_URL = 'https://g5fxjm7bsc.execute-api.ap-south-1.amazonaws.com/prod';

// ── Token helpers ─────────────────────────────────────────────────────────────
export const getToken = () => localStorage.getItem('tse_token');
export const setToken = (t) => localStorage.setItem('tse_token', t);
export const removeToken = () => localStorage.removeItem('tse_token');
export const getUser = () => {
  try { return JSON.parse(localStorage.getItem('tse_user') || 'null'); } catch { return null; }
};
export const setUser = (u) => localStorage.setItem('tse_user', JSON.stringify(u));
export const removeUser = () => localStorage.removeItem('tse_user');

// ── Core request ──────────────────────────────────────────────────────────────
async function request(method, path, body = null, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const config = { method, headers };
  if (body) config.body = JSON.stringify(body);

  const res = await fetch(`${BASE_URL}${path}`, config);
  let data;
  try {
    data = await res.json();
  } catch {
    data = { message: res.ok ? '' : `Request failed (${res.status})` };
  }

  if (!res.ok) {
    const err = new Error(data.message || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  get:    (path)         => request('GET',    path),
  post:   (path, body)   => request('POST',   path, body),
  put:    (path, body)   => request('PUT',    path, body),
  patch:  (path, body)   => request('PATCH',  path, body),
  delete: (path)         => request('DELETE', path),
};

// Uploads a raw file directly to an S3 presigned URL (obtained from a
// `*/upload` style backend endpoint). Not routed through `request()` since
// this is a plain PUT of file bytes, not a JSON API call.
export async function uploadFileToS3(uploadUrl, file) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error('File upload failed');
  return true;
}

// Generic presigned-URL request (folder-scoped) + upload in one step. Used for
// attachments that aren't tied to an already-created record id (e.g. Advance
// Request / Expense Claim attachments picked before the record exists).
export async function uploadAttachment(file, folder = 'expense') {
  const { uploadUrl, key, fileUrl } = await api.post('/upload/presigned-url', {
    fileName: file.name,
    fileType: file.type || 'application/octet-stream',
    folder,
  });
  await uploadFileToS3(uploadUrl, file);
  return { fileName: file.name, s3Key: key, fileUrl };
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersAPI = {
  list: (params = {}) => api.get('/users?' + new URLSearchParams(params)),
  get: (id) => api.get(`/users/${id}`),
  create: (body) => api.post('/users', body),
  update: (id, body) => api.put(`/users/${id}`, body),
  activate: (id) => api.post(`/users/${id}/activate`),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  hierarchy: () => api.get('/users/hierarchy'),
  // Admin password management — actual current passwords can't be viewed
  // (stored as one-way hashes); Admin can instead set a new one directly,
  // or email the user a secure reset link.
  setPassword: (id, password, notifyUser = false) => api.post(`/users/${id}/set-password`, { password, notifyUser }),
  sendResetLink: (id) => api.post(`/users/${id}/send-reset-link`),
};

// ── Letterhead ────────────────────────────────────────────────────────────────
export const letterheadAPI = {
  listTemplates: () => api.get('/letterhead/templates'),
  createRequest: (body) => api.post('/letterhead/requests', body),
  listRequests: (params = {}) => api.get('/letterhead/requests?' + new URLSearchParams(params)),
  getRequest: (id) => api.get(`/letterhead/requests/${id}`),
  approve: (id, remarks) => api.post(`/letterhead/requests/${id}/approve`, { remarks }),
  reject: (id, remarks) => api.post(`/letterhead/requests/${id}/reject`, { remarks }),
  download: (id) => api.get(`/letterhead/requests/${id}/download`),
};

// ── Expense ───────────────────────────────────────────────────────────────────
export const expenseAPI = {
  // Advance
  createAdvance: (body) => api.post('/expense/advance-requests', body),
  listAdvances: (params = {}) => api.get('/expense/advance-requests?' + new URLSearchParams(params)),
  getAdvance: (id) => api.get(`/expense/advance-requests/${id}`),
  updateAdvance: (id, body) => api.put(`/expense/advance-requests/${id}`, body),
  submitAdvance: (id) => api.post(`/expense/advance-requests/${id}/submit`),
  approveAdvance: (id, body) => api.post(`/expense/advance-requests/${id}/approve`, body),
  rejectAdvance: (id, remarks) => api.post(`/expense/advance-requests/${id}/reject`, { remarks }),
  // Claims
  createClaim: (body) => api.post('/expense/claims', body),
  listClaims: (params = {}) => api.get('/expense/claims?' + new URLSearchParams(params)),
  getClaim: (id) => api.get(`/expense/claims/${id}`),
  updateClaim: (id, body) => api.put(`/expense/claims/${id}`, body),
  submitClaim: (id) => api.post(`/expense/claims/${id}/submit`),
  approveClaim: (id, body) => api.post(`/expense/claims/${id}/approve`, body),
  rejectClaim: (id, remarks) => api.post(`/expense/claims/${id}/reject`, { remarks }),
  settleClaim: (id, body) => api.post(`/expense/claims/${id}/settle`, body),
  // Reports
  reports: (params = {}) => api.get('/expense/reports?' + new URLSearchParams(params)),
  // Attachments (presigned upload, scoped to the 'expense' folder)
  requestAttachmentUpload: (fileName, fileType) => api.post('/expense/upload', { fileName, fileType }),
  // Presigned GET URL to view/download an already-uploaded attachment
  // (the bucket is private, so the stored s3Key needs a freshly signed URL
  // each time it's opened).
  getAttachmentUrl: (s3Key) => api.get('/expense/attachment-url?key=' + encodeURIComponent(s3Key)),
};

// Opens an expense/advance attachment in a new tab using a freshly-signed
// URL. Opens the tab synchronously (inside the click handler) so browsers
// don't block it as a popup, then points it at the real URL once fetched.
export async function openExpenseAttachment(attachment) {
  if (!attachment?.s3Key) return;
  const tab = window.open('', '_blank');
  try {
    const { url } = await expenseAPI.getAttachmentUrl(attachment.s3Key);
    if (tab) tab.location.href = url;
    else window.open(url, '_blank');
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}

// ── Vendor ────────────────────────────────────────────────────────────────────
export const vendorAPI = {
  // Vendors
  listVendors: (params = {}) => api.get('/vendor/vendors?' + new URLSearchParams(params)),
  getVendor: (id) => api.get(`/vendor/vendors/${id}`),
  createVendor: (body) => api.post('/vendor/vendors', body),
  updateVendor: (id, body) => api.put(`/vendor/vendors/${id}`, body),
  approveVendor: (id, body) => api.post(`/vendor/vendors/${id}/approve`, body),
  rejectVendor: (id, body) => api.post(`/vendor/vendors/${id}/reject`, body),
  bulkImportVendors: (vendors) => api.post('/vendor/vendors/bulk-import', { vendors }),
  // Projects
  listProjects: (params = {}) => api.get('/vendor/projects?' + new URLSearchParams(params)),
  createProject: (body) => api.post('/vendor/projects', body),
  updateProject: (id, body) => api.put(`/vendor/projects/${id}`, body),
  // Payment Requests
  listPaymentRequests: (params = {}) => api.get('/vendor/payment-requests?' + new URLSearchParams(params)),
  getPaymentRequest: (id) => api.get(`/vendor/payment-requests/${id}`),
  createPaymentRequest: (body) => api.post('/vendor/payment-requests', body),
  approvePaymentRequest: (id, body) => api.post(`/vendor/payment-requests/${id}/approve`, body),
  rejectPaymentRequest: (id, body) => api.post(`/vendor/payment-requests/${id}/reject`, body),
  requestPoAttachmentUpload: (id, body) => api.post(`/vendor/payment-requests/${id}/po-attachment`, body),
  // GRN
  listGRN: (params = {}) => api.get('/vendor/grn?' + new URLSearchParams(params)),
  updateGRN: (id, body) => api.put(`/vendor/grn/${id}`, body),
  // Invoice Upload
  uploadInvoice: (body) => api.post('/vendor/invoice-upload', body),
  // Finance
  listFinanceQueue: (params = {}) => api.get('/vendor/finance?' + new URLSearchParams(params)),
  processPayment: (id, body) => api.post(`/vendor/finance/${id}/process`, body),
  // Payout
  listReleased: (params = {}) => api.get('/vendor/payment-released?' + new URLSearchParams(params)),
  payout: (id) => api.post(`/vendor/payout/${id}`),
  // Reports
  reports: (params = {}) => api.get('/vendor/reports?' + new URLSearchParams(params)),
};

// ── HR: Business Card Request ────────────────────────────────────────────────
export const businessCardAPI = {
  create: (body) => api.post('/hr/business-cards', body),
  list: (params = {}) => api.get('/hr/business-cards?' + new URLSearchParams(params)),
  get: (id) => api.get(`/hr/business-cards/${id}`),
  approve: (id, body) => api.post(`/hr/business-cards/${id}/approve`, body),
  reject: (id, body) => api.post(`/hr/business-cards/${id}/reject`, body),
};

// ── Complaint Box ────────────────────────────────────────────────────────────
export const complaintsAPI = {
  create: (body) => api.post('/complaints', body),
  list: (params = {}) => api.get('/complaints?' + new URLSearchParams(params)),
  get: (id) => api.get(`/complaints/${id}`),
  updateStatus: (id, body) => api.post(`/complaints/${id}/status`, body),
};

// ── Query-string helper (drops empty values) ─────────────────────────────────
export const qs = (params = {}) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

// Opens any stored attachment ({ s3Key, fileName }) in a new tab using a
// freshly-signed URL (bucket is private).
export const openAttachment = (attachment) => openExpenseAttachment(attachment);

// ── eTAMS: travel requests, receipt centre, policy, overview ─────────────────
export const etamsAPI = {
  overview: () => api.get('/etams/overview'),
  policy: () => api.get('/etams/policy'),
  savePolicy: (policy) => api.put('/etams/policy', { policy }),
  // Travel requests (TAE)
  listTravel: (params = {}) => api.get('/etams/travel-requests' + qs(params)),
  travelStats: () => api.get('/etams/travel-requests/stats'),
  getTravel: (id) => api.get(`/etams/travel-requests/${id}`),
  createTravel: (body) => api.post('/etams/travel-requests', body),
  updateTravel: (id, body) => api.put(`/etams/travel-requests/${id}`, body),
  submitTravel: (id) => api.post(`/etams/travel-requests/${id}/submit`),
  actTravel: (id, body) => api.post(`/etams/travel-requests/${id}/action`, body),
  cancelTravel: (id, remarks) => api.post(`/etams/travel-requests/${id}/cancel`, { remarks }),
  deleteTravel: (id) => api.delete(`/etams/travel-requests/${id}`),
  // Receipt centre
  listReceipts: (params = {}) => api.get('/etams/receipts' + qs(params)),
  createReceipt: (body) => api.post('/etams/receipts', body),
  updateReceipt: (id, body) => api.put(`/etams/receipts/${id}`, body),
  assignReceipt: (id, claimId) => api.post(`/etams/receipts/${id}/assign`, { claimId }),
  deleteReceipt: (id) => api.delete(`/etams/receipts/${id}`),
  // Claims
  returnClaim: (id, remarks) => api.post(`/etams/claims/${id}/return`, { remarks }),
};

// ── TSE Workspace ────────────────────────────────────────────────────────────
export const wsAPI = {
  // People & profile
  directory: (params = {}) => api.get('/workspace/directory' + qs(params)),
  profile: () => api.get('/workspace/profile'),
  updateProfile: (body) => api.put('/workspace/profile', body),
  saveMapping: (userId, body) => api.put(`/workspace/users/${userId}/mapping`, body),
  userProfile: (userId) => api.get(`/workspace/users/${userId}/profile`),
  // Notifications & search
  notifications: () => api.get('/workspace/notifications'),
  readNotification: (id) => api.post(`/workspace/notifications/${id}/read`),
  readAllNotifications: () => api.post('/workspace/notifications/read-all'),
  search: (q) => api.get('/workspace/search' + qs({ q })),
  inbox: () => api.get('/workspace/approvals/inbox'),
  // Settings / projects / audit
  getSetting: (key) => api.get(`/workspace/settings/${key}`),
  saveSetting: (key, value) => api.put(`/workspace/settings/${key}`, { value }),
  projects: (params = {}) => api.get('/workspace/projects' + qs(params)),
  codePreview: (params = {}) => api.get('/workspace/projects/code-preview' + qs(params)),
  auditLog: (params = {}) => api.get('/workspace/audit-log' + qs(params)),
  // Generic requests engine
  listRequests: (params = {}) => api.get('/workspace/requests' + qs(params)),
  requestStats: (params = {}) => api.get('/workspace/requests/stats' + qs(params)),
  getRequest: (id) => api.get(`/workspace/requests/${id}`),
  createRequest: (body) => api.post('/workspace/requests', body),
  updateRequest: (id, body) => api.put(`/workspace/requests/${id}`, body),
  submitRequest: (id) => api.post(`/workspace/requests/${id}/submit`),
  actRequest: (id, body) => api.post(`/workspace/requests/${id}/action`, body),
  patchRequestData: (id, data, remarks) => api.patch(`/workspace/requests/${id}/data`, { data, remarks }),
  commentRequest: (id, remarks) => api.post(`/workspace/requests/${id}/comment`, { remarks }),
  cancelRequest: (id, remarks) => api.post(`/workspace/requests/${id}/cancel`, { remarks }),
  acknowledgeRequest: (id, remarks) => api.post(`/workspace/requests/${id}/acknowledge`, { remarks }),
  deleteRequest: (id) => api.delete(`/workspace/requests/${id}`),
  // Employee wall
  posts: (params = {}) => api.get('/workspace/wall/posts' + qs(params)),
  createPost: (body) => api.post('/workspace/wall/posts', body),
  likePost: (id) => api.post(`/workspace/wall/posts/${id}/like`),
  commentPost: (id, text) => api.post(`/workspace/wall/posts/${id}/comments`, { text }),
  deleteComment: (id, cid) => api.delete(`/workspace/wall/posts/${id}/comments/${cid}`),
  reportPost: (id, reason) => api.post(`/workspace/wall/posts/${id}/report`, { reason }),
  pinPost: (id) => api.post(`/workspace/wall/posts/${id}/pin`),
  deletePost: (id) => api.delete(`/workspace/wall/posts/${id}`),
  events: (params = {}) => api.get('/workspace/wall/events' + qs(params)),
  createEvent: (body) => api.post('/workspace/wall/events', body),
  deleteEvent: (id) => api.delete(`/workspace/wall/events/${id}`),
  celebrations: () => api.get('/workspace/wall/celebrations'),
  quickLinks: () => api.get('/workspace/wall/quick-links'),
  // Policy center
  policies: (params = {}) => api.get('/workspace/policies' + qs(params)),
  policyStats: () => api.get('/workspace/policies/stats'),
  getPolicy: (id) => api.get(`/workspace/policies/${id}`),
  createPolicy: (body) => api.post('/workspace/policies', body),
  updatePolicy: (id, body) => api.put(`/workspace/policies/${id}`, body),
  acknowledgePolicy: (id) => api.post(`/workspace/policies/${id}/acknowledge`),
  policyAcks: (id) => api.get(`/workspace/policies/${id}/acknowledgements`),
  // Cost centers & categories
  costCenters: () => api.get('/workspace/cost-centers'),
  costCenterStats: () => api.get('/workspace/cost-centers/stats'),
  createCostCenter: (body) => api.post('/workspace/cost-centers', body),
  updateCostCenter: (id, body) => api.put(`/workspace/cost-centers/${id}`, body),
  deleteCostCenter: (id) => api.delete(`/workspace/cost-centers/${id}`),
  costCategories: () => api.get('/workspace/cost-categories'),
  createCostCategory: (body) => api.post('/workspace/cost-categories', body),
  updateCostCategory: (id, body) => api.put(`/workspace/cost-categories/${id}`, body),
  projectMappings: () => api.get('/workspace/project-mappings'),
  saveProjectMapping: (code, body) => api.put(`/workspace/project-mappings/${encodeURIComponent(code)}`, body),
  desks: (params = {}) => api.get('/workspace/desks' + qs(params)),
  // Engineering
  engDocuments: (params = {}) => api.get('/workspace/engineering/documents' + qs(params)),
  engStats: (params = {}) => api.get('/workspace/engineering/stats' + qs(params)),
  createEngDocument: (body) => api.post('/workspace/engineering/documents', body),
  updateEngDocument: (id, body) => api.put(`/workspace/engineering/documents/${id}`, body),
  checkoutEngDocument: (id) => api.post(`/workspace/engineering/documents/${id}/checkout`),
  uploadRevision: (id, body) => api.post(`/workspace/engineering/documents/${id}/revision`, body),
  engDocumentStatus: (id, action, remarks) => api.post(`/workspace/engineering/documents/${id}/status`, { action, remarks }),
  transmittals: (params = {}) => api.get('/workspace/engineering/transmittals' + qs(params)),
  createTransmittal: (body) => api.post('/workspace/engineering/transmittals', body),
  // Procurement & stores
  inventory: (params = {}) => api.get('/workspace/inventory' + qs(params)),
  createInventory: (body) => api.post('/workspace/inventory', body),
  updateInventory: (id, body) => api.put(`/workspace/inventory/${id}`, body),
  stockTxn: (body) => api.post('/workspace/inventory/transactions', body),
  stockTxns: (params = {}) => api.get('/workspace/inventory/transactions' + qs(params)),
  dispatches: (params = {}) => api.get('/workspace/dispatches' + qs(params)),
  createDispatch: (body) => api.post('/workspace/dispatches', body),
  trackDispatch: (id, body) => api.post(`/workspace/dispatches/${id}/track`, body),
  // Operations
  workOrders: (params = {}) => api.get('/workspace/work-orders' + qs(params)),
  workOrderStats: (params = {}) => api.get('/workspace/work-orders/stats' + qs(params)),
  createWorkOrder: (body) => api.post('/workspace/work-orders', body),
  updateWorkOrder: (id, body) => api.post(`/workspace/work-orders/${id}/update`, body),
  siteLogs: (params = {}) => api.get('/workspace/site-logs' + qs(params)),
  createSiteLog: (body) => api.post('/workspace/site-logs', body),
  reviewSiteLog: (id, remarks) => api.post(`/workspace/site-logs/${id}/review`, { remarks }),
  // EHS
  ehsStats: (params = {}) => api.get('/workspace/ehs/stats' + qs(params)),
  observations: (params = {}) => api.get('/workspace/ehs/observations' + qs(params)),
  createObservation: (body) => api.post('/workspace/ehs/observations', body),
  advanceObservation: (id, remarks) => api.post(`/workspace/ehs/observations/${id}/advance`, { remarks }),
  capas: (params = {}) => api.get('/workspace/ehs/capa' + qs(params)),
  createCapa: (body) => api.post('/workspace/ehs/capa', body),
  updateCapa: (id, body) => api.put(`/workspace/ehs/capa/${id}`, body),
  ehsRecords: (params = {}) => api.get('/workspace/ehs/records' + qs(params)),
  createEhsRecord: (body) => api.post('/workspace/ehs/records', body),
  updateEhsRecord: (id, body) => api.put(`/workspace/ehs/records/${id}`, body),
  // Finance & cashbook
  financeQueue: (params = {}) => api.get('/workspace/finance/queue' + qs(params)),
  createPayment: (body) => api.post('/workspace/finance/payments', body),
  paymentChecklist: (id, body) => api.post(`/workspace/finance/payments/${id}/checklist`, body),
  paymentAction: (id, body) => api.post(`/workspace/finance/payments/${id}/action`, body),
  financeVendors: () => api.get('/workspace/finance/vendors'),
  cashDay: (params = {}) => api.get('/workspace/cashbook/day' + qs(params)),
  cashEntries: (params = {}) => api.get('/workspace/cashbook/entries' + qs(params)),
  createCashEntry: (body) => api.post('/workspace/cashbook/entries', body),
  reconcileCashEntry: (id, reconciled) => api.post(`/workspace/cashbook/entries/${id}/reconcile`, { reconciled }),
  paymentLinks: () => api.get('/workspace/cashbook/payment-links'),
  prepareVoucher: (id, body) => api.post(`/workspace/cashbook/payment-links/${id}/voucher`, body),
  dayClose: (body) => api.post('/workspace/cashbook/day-close', body),
  dayCloseDecision: (id, decision, remarks) => api.post(`/workspace/cashbook/day-close/${id}/${decision}`, { remarks }),
  // Admin console
  accessStats: () => api.get('/workspace/admin/access-stats'),
  delegations: () => api.get('/workspace/admin/delegations'),
  createDelegation: (body) => api.post('/workspace/admin/delegations', body),
  delegationDecision: (id, decision) => api.post(`/workspace/admin/delegations/${id}/${decision}`),
  saveOffboarding: (userId, body) => api.put(`/workspace/admin/users/${userId}/offboarding`, body),
  workflowRules: () => api.get('/workspace/admin/workflow-rules'),
  saveWorkflowRule: (body) => api.post('/workspace/admin/workflow-rules', body),
  workflowRuleDecision: (id, decision) => api.post(`/workspace/admin/workflow-rules/${id}/${decision}`),
  integrations: () => api.get('/workspace/admin/integrations'),
  updateIntegration: (id, body) => api.put(`/workspace/admin/integrations/${id}`, body),
  testIntegration: (id) => api.post(`/workspace/admin/integrations/${id}/test`),
  // Business card dispatch
  dispatchBusinessCard: (id, status, remarks) => api.post(`/workspace/business-cards/${id}/dispatch`, { status, remarks }),
};
